
// Web Crypto API helper for Digital Signatures (RSA-PSS)

const ALGORITHM = {
  name: "RSA-PSS",
  modulusLength: 2048,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: "SHA-256",
};

export const generateKeyPair = async () => {
  try {
    const keyPair = await window.crypto.subtle.generateKey(
      ALGORITHM,
      true, // extractable
      ["sign", "verify"]
    );
    return keyPair;
  } catch (error) {
    console.error("Error generating key pair:", error);
    throw error;
  }
};

export const exportKey = async (key, type = "spki") => {
  // type: "spki" for public, "pkcs8" for private
  try {
    const exported = await window.crypto.subtle.exportKey(type, key);
    const exportedAsBase64 = arrayBufferToBase64(exported);
    return exportedAsBase64;
  } catch (error) {
    console.error("Error exporting key:", error);
    throw error;
  }
};

export const importKey = async (pem, type = "spki", usage = ["verify"]) => {
  try {
    // pem is base64 string
    const binaryDerString = window.atob(pem);
    const binaryDer = str2ab(binaryDerString);

    const key = await window.crypto.subtle.importKey(
      type,
      binaryDer,
      ALGORITHM,
      true,
      usage
    );
    return key;
  } catch (error) {
    console.error("Error importing key:", error);
    throw error;
  }
};

export const signData = async (privateKey, data) => {
  try {
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);
    
    const signature = await window.crypto.subtle.sign(
      {
        name: "RSA-PSS",
        saltLength: 32,
      },
      privateKey,
      encodedData
    );

    return arrayBufferToBase64(signature);
  } catch (error) {
    console.error("Error signing data:", error);
    throw error;
  }
};

export const verifySignature = async (publicKey, signature, data) => {
  try {
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);
    const signatureBuffer = base64ToArrayBuffer(signature);

    const isValid = await window.crypto.subtle.verify(
      {
        name: "RSA-PSS",
        saltLength: 32,
      },
      publicKey,
      signatureBuffer,
      encodedData
    );

    return isValid;
  } catch (error) {
    console.error("Error verifying signature:", error);
    return false;
  }
};

// Helpers
function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function base64ToArrayBuffer(base64) {
  const binary_string = window.atob(base64);
  const len = binary_string.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i);
  }
  return bytes.buffer;
}

function str2ab(str) {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
}

// Storage Helpers (localStorage)
export const storeKeys = async (keyPair) => {
  const publicKey = await exportKey(keyPair.publicKey, "spki");
  const privateKey = await exportKey(keyPair.privateKey, "pkcs8");
  
  localStorage.setItem("dak_public_key", publicKey);
  localStorage.setItem("dak_private_key", privateKey);
  
  return { publicKey, privateKey };
};

export const getStoredKeys = async () => {
  const publicKeyPem = localStorage.getItem("dak_public_key");
  const privateKeyPem = localStorage.getItem("dak_private_key");

  if (!publicKeyPem || !privateKeyPem) return null;

  try {
    const publicKey = await importKey(publicKeyPem, "spki", ["verify"]);
    const privateKey = await importKey(privateKeyPem, "pkcs8", ["sign"]);
    return { publicKey, privateKey, publicKeyPem, privateKeyPem };
  } catch (error) {
    console.error("Error loading stored keys:", error);
    return null;
  }
};
